/**
 * React Query hooks for vitals monitoring
 */
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { getMyVitals, createMyVitals, getVitals, createVitals, vitalsApi } from '../api/vitals';
import type { Vitals } from '../api/types';
import { toast } from 'sonner';

// Query Keys
export const vitalsKeys = {
  all: ['vitals'] as const,
  lists: () => [...vitalsKeys.all, 'list'] as const,
  list: (patientId: number, startDate?: string, endDate?: string) =>
    [...vitalsKeys.lists(), patientId, { startDate, endDate }] as const,
  myList: (startDate?: string, endDate?: string) =>
    [...vitalsKeys.lists(), 'me', { startDate, endDate }] as const,
  trends: () => [...vitalsKeys.all, 'trends'] as const,
  trend: (patientId: number, metric?: string, days?: number) =>
    [...vitalsKeys.trends(), patientId, { metric, days }] as const,
};

/**
 * Hook to fetch vitals for current patient user
 */
export function useMyVitals(
  startDate?: string,
  endDate?: string,
  options?: Omit<UseQueryOptions<Vitals[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: vitalsKeys.myList(startDate, endDate),
    queryFn: () => getMyVitals(startDate, endDate),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchInterval: false,
    ...options,
  });
}

/**
 * Hook to create vitals for current patient user
 */
export function useCreateMyVitals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      heart_rate?: number;
      spo2?: number;
      systolic?: number;
      diastolic?: number;
      temperature?: number;
      respiratory_rate?: number;
      weight?: number;
      blood_glucose?: number;
      pain_level?: number;
      notes?: string;
      location?: string;
    }) => createMyVitals(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vitalsKeys.myList() });
      toast.success('Vitals recorded successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to record vitals');
    },
  });
}

/**
 * Hook to fetch vitals for a patient
 */
export function useVitals(
  patientId: number,
  startDate?: string,
  endDate?: string,
  options?: Omit<UseQueryOptions<Vitals[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: vitalsKeys.list(patientId, startDate, endDate),
    queryFn: () => getVitals(patientId, startDate, endDate),
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to create vitals for a patient (for healthcare providers)
 */
export function useCreateVitals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      patient_id: number;
      heart_rate?: number;
      spo2?: number;
      systolic?: number;
      diastolic?: number;
      temperature?: number;
      respiratory_rate?: number;
      weight?: number;
      blood_glucose?: number;
      pain_level?: number;
      notes?: string;
      location?: string;
    }) => createVitals(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: vitalsKeys.list(variables.patient_id) });
      toast.success('Vitals recorded successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to record vitals');
    },
  });
}

/**
 * Hook to fetch vitals trends
 */
export function useVitalsTrends(
  patientId: number,
  metric?: string,
  days?: number
) {
  return useQuery({
    queryKey: vitalsKeys.trend(patientId, metric, days),
    queryFn: () => vitalsApi.getVitalsTrends(patientId, { metric, days }),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch latest vitals
 */
export function useLatestVitals(patientId: number) {
  return useQuery({
    queryKey: [...vitalsKeys.all, 'latest', patientId],
    queryFn: () => vitalsApi.getLatestVitals(patientId),
    enabled: !!patientId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to update vitals record
 */
export function useUpdateVitals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vitalsId, data }: {
      vitalsId: number;
      data: {
        heart_rate?: number;
        spo2?: number;
        systolic_bp?: number;
        diastolic_bp?: number;
        temperature?: number;
        respiratory_rate?: number;
        weight?: number;
        notes?: string;
      }
    }) => vitalsApi.updateVitals(vitalsId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vitalsKeys.all });
      toast.success('Vitals updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update vitals');
    },
  });
}

/**
 * Hook to delete vitals record
 */
export function useDeleteVitals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vitalsId: number) => vitalsApi.deleteVitals(vitalsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vitalsKeys.all });
      toast.success('Vitals record deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete vitals');
    },
  });
}
